import type { FormElement, Option } from '@common/types/form-element.type';
import type { FormButtonSettings } from '@components/sp-form-button-settings';
import type { FormInputSettings } from '@components/sp-form-input-settings';
import type { Observable } from 'rxjs';
import type { SafeHtml } from '@angular/platform-browser';

import { ChangeDetectionStrategy, Component, inject, type OnInit } from '@angular/core';

import {
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
    EButtonStyleType,
    EPaymentType,
    EFormInputType,
    ESize,
} from '@common/enums';
import * as typeGuards from '@common/type-guards/form-elements.type-guards';

import { WebBaseViewComponent } from '@web/widgets/common/abstract/web-base-view.component';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';

import { getStyles } from './shop-cart-view.component.styles';
import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from '@moddi3/ngx-intl-tel-input';
import { CountryISOService } from '@common/services/REST/country-iso.http.service';
import type { ShopCartWidgetData } from '@web/widgets/shop-cart/type';
import type { ProductCard } from '@common/types/product-card.type';
import type { PaymentFormWidgetData } from '@web/widgets/payment-form/type';
import { paymentsList, paymentTypeNames } from '@common/constants/payment-form.const';
import type { PaymentElement, PaymentMethod } from '@integrations/payment-methods';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { RemovePTags } from '@common/pipes/remove-p-tag.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import type { FormAdditionalSettings } from '@components/sp-form-additional-settings';
import { formatFileUrl, formatFileUrlWithResize, resolveCdnBaseUrl } from '@components/sp-file-manager/src/helpers';
import type { SelectedFileExtended } from '@common/types/file.type';
import { SpDirectivesModule } from '@common/directives/directives.module';
import { environment } from '@web-env/environment';
import { SpIntlTelInputA11yDirective } from '@common/directives/intl-tel-input-a11y.directive';

@Component({
    selector: 'web-shop-cart-view',
    templateUrl: './shop-cart-view.component.html',
    styleUrls: ['./shop-cart-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        NgxIntlTelInputModule,
        SpIntlTelInputA11yDirective,
        ReactiveFormsModule,
        TranslateModule,
        RemovePTags,
        SpDirectivesModule,
    ],
})
export class ShopCartViewComponent extends WebBaseViewComponent<ShopCartWidgetData> implements OnInit {
    private countryISOService: CountryISOService = inject(CountryISOService);
    private settingsFacade: SettingsFacade = inject(SettingsFacade);

    public readonly SearchCountryField = SearchCountryField;
    public readonly PhoneNumberFormat = PhoneNumberFormat;
    public readonly typeGuards = typeGuards;
    public readonly formInputType = EFormInputType;
    public readonly paymentsList = paymentsList;
    public readonly excludeCountries = [CountryISO.Russia];

    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;

    public readonly shopSettings$ = this.settingsFacade.shopSettings$;
    public CountryISO = CountryISO.Ukraine;
    protected getStyles = getStyles;

    public ngOnInit(): void {
        super.ngOnInit();

        this.getCountryISO();
    }

    public getButtonEffectClass(button: FormButtonSettings): string {
        let nameEffect: string = '';

        if (button.additionalEffects?.isActive) {
            if (button.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.hover) {
                switch (button.additionalEffects?.hoverEffects) {
                    case EButtonAdditionalHoverEffectsTypes.bounce:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.bounce;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.shake:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.shake;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.decrease:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.decrease;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.increase:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.increase;
                        break;
                    default:
                        break;
                }
            }

            if (button.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.static) {
                switch (button.additionalEffects?.staticEffects) {
                    case EButtonAdditionalStaticEffectsTypes.shine:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.shine;
                        break;
                    case EButtonAdditionalStaticEffectsTypes.twinkle:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.twinkle;
                        break;
                    default:
                        break;
                }
            }

            return 'button_effect_' + nameEffect;
        }

        return '';
    }

    public getButtonStyles(formButtonSettings: FormButtonSettings): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[formButtonSettings.size ? formButtonSettings.size : ESize.M]}`;

        if (formButtonSettings.styleType === EButtonStyleType.custom) {
            return `${styles} ${this.classes?.buttonStyles} ${this.getButtonEffectClass(formButtonSettings)}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[formButtonSettings.styleType]} ${
            this.classes?.buttonStyles
        } ${this.getButtonEffectClass(formButtonSettings)}`;
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }

    public getSelected(options: Option[]) {
        return !options.some((el: Option) => el.selected);
    }

    public get elements(): FormElement[] {
        return this.widgetData.elements;
    }

    public get paymentElements(): PaymentFormWidgetData['paymentElements'] {
        return this.widgetData.paymentElements;
    }

    public get paymentFormName(): ShopCartWidgetData['paymentFormName'] {
        return this.widgetData.paymentFormName;
    }

    public get items(): ProductCard[] {
        return this.widgetData.items;
    }

    public get inputSettings(): FormInputSettings {
        return this.widgetData.inputSettings;
    }

    public get cartIconSettings(): ShopCartWidgetData['cartIconSettings'] {
        return this.widgetData.cartIconSettings;
    }

    public get formButtonSettings(): FormButtonSettings {
        return this.widgetData.formButtonSettings;
    }

    public get additionalFormSettings(): ShopCartWidgetData['additionalFormSettings'] {
        return this.widgetData.additionalFormSettings;
    }

    public get promoCodeSettings(): FormAdditionalSettings['promoCodeSettings'] {
        return this.additionalFormSettings?.promoCodeSettings;
    }

    public get isPromoCodeEnabled(): boolean {
        return this.promoCodeSettings?.isEnabled;
    }

    public get promoCodePlaceholder(): string {
        return this.promoCodeSettings?.placeholder;
    }

    public get promoCodeLabel(): string {
        return this.promoCodeSettings?.label;
    }

    public trackByElementIndex(index: number): number {
        return index;
    }

    private getCountryISO() {
        this.countryISOService.getCountryISO().subscribe((data) => {
            this.CountryISO = data.country_code.toLowerCase();
            this.changeDetectorRef.detectChanges();
        });
    }

    public getTotalPrice(): number {
        let total = 0;
        this.items.forEach((item) => (total += Number(item.price) * item.quantity));
        return total;
    }

    public hasPricedDeliveryVariant(variants: any[]): boolean {
        return !!variants?.some((variant) => variant?.currentDeliveryValueState !== 1);
    }

    public getPaymentIconSrc(type: PaymentMethod['type']): string {
        return `./assets/img/websites/payment-icons/sm/${paymentTypeNames[type]}-sm.svg`;
    }

    public getProductImageSrc(item: ProductCard): string {
        const image = item.images?.[0];
        if (!image) return '';
        if (!image.url || image.url.startsWith('./') || image.url.startsWith('http')) {
            return formatFileUrl(image, '');
        }
        return formatFileUrlWithResize(image, this.resolveProductImageBaseUrl(image), 720);
    }

    public getProductImageFallbackSrc(item: ProductCard): string {
        const image = item.images?.[0];
        if (!image) return '';
        if (!image.url || image.url.startsWith('./') || image.url.startsWith('http')) {
            return formatFileUrl(image, '');
        }
        return formatFileUrl(image, this.resolveProductImageBaseUrl(image));
    }

    private resolveProductImageBaseUrl(image: SelectedFileExtended): string {
        return resolveCdnBaseUrl(image?.defaultCdnUrl, environment.fileManagerCdnUrl);
    }

    public getPaymentElementLabel(paymentElementType: PaymentElement['type'], index: number): string {
        if (paymentElementType === EPaymentType.custom) {
            return this.paymentElements.at(index).customPaymentName;
        }

        return this.paymentElements.at(index)?.newPaymentName || this.paymentsList[paymentElementType]?.label;
    }
}
